import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, ShieldCheck, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, brl, fmtDT, formatApiError } from "@/lib/api";
import { PlanEditDialog } from "@/components/PlanEditDialog";

const LinkStatus = ({ p }) => {
  const c = p.link_check;
  if (!c || c.link !== p.mp_link) return <span className="mono-badge badge-error">Não verificado</span>;
  return c.ok && c.amount_ok
    ? <span className="mono-badge badge-ai inline-flex gap-1"><CheckCircle2 className="w-3 h-3" />OK {brl(c.amount_found_cents)}</span>
    : <span className="mono-badge badge-danger inline-flex gap-1"><AlertTriangle className="w-3 h-3" />{c.error}</span>;
};

const Config = ({ cfg, setCfg }) => {
  const save = async (patch) => {
    try { setCfg((await api.put("/sales/config", { ...cfg, ...patch })).data); toast.success("Configuração salva"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <section className="panel-card space-y-3" data-testid="sales-config">
      <div className="flex items-center justify-between gap-4 mode-option">
        <div><p className="text-sm font-medium">Enviar links de pagamento ao público</p><p className="text-xs text-[#9CA3AF]">Desligado: só o Simulador recebe links; clientes reais vão para atendimento humano. Só ligue após validar a elegibilidade no Mercado Pago e a autorização dos conteúdos.</p></div>
        <Switch checked={cfg.payment_links_live} onCheckedChange={(v) => save({ payment_links_live: v })} data-testid="links-live-switch" />
      </div>
      <div className="flex items-center justify-between gap-4 mode-option">
        <div><p className="text-sm font-medium">Bloquear teste grátis repetido</p><p className="text-xs text-[#9CA3AF]">Só age quando o WhatsApp envia o número do cliente (contato não salvo). Nunca bloqueia por nome.</p></div>
        <Switch checked={cfg.trial_block_enabled} onCheckedChange={(v) => save({ trial_block_enabled: v })} data-testid="trial-block-switch" />
      </div>
    </section>
  );
};

export const PlansPanel = () => {
  const [plans, setPlans] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/plans").then((r) => setPlans(r.data)), []);
  useEffect(() => { load(); api.get("/sales/config").then((r) => setCfg(r.data)); }, [load]);
  const verify = async () => {
    setBusy(true);
    try { setPlans((await api.post("/plans/verify-links")).data); toast.success("Links verificados"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="space-y-6">
      {cfg && <Config cfg={cfg} setCfg={setCfg} />}
      <section className="panel-card overflow-x-auto" data-testid="plans-table">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><h2 className="font-heading text-base md:text-lg font-semibold">Planos e links fixos do Mercado Pago</h2><p className="text-xs text-[#6B7280] mt-1">A IA só usa estes valores e o sistema só envia o link do plano escolhido.</p></div>
          <Button onClick={verify} disabled={busy} className="btn-primary h-10" data-testid="verify-links-button"><ShieldCheck className="w-4 h-4 mr-2" />{busy ? "Verificando..." : "Verificar todos os links"}</Button>
        </div>
        <table className="w-full mt-4 min-w-[720px]">
          <thead><tr className="text-left text-xs text-[#6B7280] font-mono uppercase"><th>ID</th><th>Plano</th><th>Preço</th><th>Link</th><th>Verificação</th><th>Venda</th><th /></tr></thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id} className="border-t border-[#1F2937] text-sm" data-testid={`plan-row-${p.id}`}>
                <td className="py-2 font-mono text-xs text-[#10B981]">{p.id}</td>
                <td className="py-2">{p.label}{p.description && <p className="text-xs text-[#6B7280]">{p.description}</p>}</td>
                <td className="py-2 font-mono" data-testid={`plan-price-${p.id}`}>{brl(p.price_cents)}</td>
                <td className="py-2 font-mono text-xs"><a href={p.mp_link} target="_blank" rel="noreferrer" className="text-[#22D3EE]" data-testid={`plan-link-${p.id}`}>{p.mp_link?.replace("https://", "")}</a></td>
                <td className="py-2" title={p.link_check ? fmtDT(p.link_check.checked_at) : ""} data-testid={`plan-check-${p.id}`}><LinkStatus p={p} /></td>
                <td className="py-2"><span className={`mono-badge ${p.active ? "badge-ai" : "badge-muted"}`}>{p.active ? "Ativo" : "Oculto"}</span></td>
                <td className="py-2"><button onClick={() => setEdit(p)} className="text-[#9CA3AF] hover:text-[#10B981]" data-testid={`plan-edit-${p.id}`}><Pencil className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {edit && <PlanEditDialog p={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
};
